// apps/widget/src/components/capture-tips.tsx
//
// What makes a good photo, shown just before the camera opens — on the
// computer and on the phone. Every one of these is a reason a scan comes back
// wrong: glasses and a fringe hide the eye area and forehead, poor light reads
// as dull or uneven skin, makeup hides spots and redness, and a face off-centre
// or at an angle throws every region out.

import { t } from '../lib/i18n';

const TIPS = [
  { key: 'glasses', icon: GlassesIcon },
  { key: 'light', icon: LightIcon },
  { key: 'makeup', icon: MakeupIcon },
  { key: 'position', icon: FaceIcon },
] as const;

export function CaptureTips() {
  return (
    <ol className="gs-tips">
      {TIPS.map(({ key, icon: Icon }, i) => (
        <li key={key} className="gs-tip">
          <span className="gs-tip__icon" aria-hidden="true">
            <Icon />
            <b>{i + 1}</b>
          </span>
          <span className="gs-tip__text">
            <strong>{t(`prepare.${key}.title`)}</strong>
            <span>{t(`prepare.${key}.body`)}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** The tips with their heading and button — the screen on the computer, and the phone's first step. */
export function PrepareContent({ onReady, readyLabel }: { onReady: () => void; readyLabel?: string }) {
  return (
    <div className="gs-prepare gs-stack">
      <div className="gs-prepare__head">
        <h1 className="gs-title">{t('prepare.title')}</h1>
        <p className="gs-muted">{t('prepare.subtitle')}</p>
      </div>
      <CaptureTips />
      <button className="gs-btn gs-btn--primary" onClick={onReady}>
        {readyLabel ?? t('prepare.ready')}
      </button>
    </div>
  );
}

const stroke = { stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };

function GlassesIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
      <circle cx="6.5" cy="14" r="3.5" />
      <circle cx="17.5" cy="14" r="3.5" />
      <path d="M10 14c1.2-1 2.8-1 4 0M3 13l1.5-5M21 13l-1.5-5" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

function LightIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
    </svg>
  );
}

function MakeupIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
      <rect x="8.5" y="14" width="7" height="7.5" rx="1" />
      <path d="M9.5 14v-3h5v3" />
      <path d="M10.5 11V6.8L13.5 4.5V11" />
    </svg>
  );
}

function FaceIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
      <circle cx="12" cy="12" r="9.5" strokeDasharray="2.5 2.5" />
      <ellipse cx="12" cy="12" rx="4.2" ry="5.4" />
      <path d="M10.3 11h.01M13.7 11h.01M10.8 14.3c.7.5 1.7.5 2.4 0" />
    </svg>
  );
}
